import { Signal } from "../core/Signal.js";
import { Url } from "../text/Url.js";
import { object } from "../util/ObjectUtil.js";
import { LoaderMap, LoaderOptionsMap } from "./LoaderMap.js";

export interface AbstractLoaderOptions {
    cache?: boolean;
    onProgress?: (evt: LoaderProgress) => void;
}

export interface LoaderProgress {
    loaded: number;
    total: number;
    delta: number;
    event: ProgressEvent;
}

export abstract class AbstractLoader<Response, Options extends AbstractLoaderOptions> implements Promise<Response>{
    [Symbol.toStringTag]: string;

    protected _onProgress = Signal.create<[LoaderProgress]>();
    protected _onFinally = Signal.create<[Response | RejectReason]>();

    public readonly promise: Promise<Response>;

    public readonly url: string;

    protected _options: Options;

    private _resolve: (image: Response) => void
    private _reject: (reason: RejectReason) => void;

    constructor(url: string, options: Options = null) {

        this._options = object.merge(this.defaultOptions(), (options || {}) as any);
        this.url = url;
        if (this._options.cache !== undefined && !this._options.cache) {
            this.url = Url.params.set("_", Date.now() + "");
        }

        if (typeof this._options.onProgress === "function") {
            this._onProgress.add(this._options.onProgress);
        }

        this.promise = new Promise<Response>(
            (res, rej) => {
                this._resolve = res;
                this._reject = rej;
            }
        );
    }

    protected resolve(result: Response): void {
        this._resolve(result);
        this._onFinally.dispatch(result);
        this.finish();
    }

    protected reject(reason: any): void {
        var o: RejectReason = { reason: reason, loader: this };
        this._reject(o);
        this._onFinally.dispatch(o);
        this.finish();
    }

    public then<TResult1 = Response, TResult2 = never>(onfulfilled?: (value: Response) => TResult1 | PromiseLike<TResult1>, onrejected?: (reason: any) => TResult2 | PromiseLike<TResult2>): Promise<TResult1 | TResult2> {
        return this.promise.then(onfulfilled, onrejected);
    }

    public catch<TResult = never>(onrejected?: (reason: RejectReason) => TResult | PromiseLike<TResult>): Promise<Response | TResult> {
        return this.promise.catch(onrejected);
    }

    public finally(onfinally?: (() => void) | undefined | null): Promise<Response> {
        return this._onFinally.promise(true).then(onfinally as any);
    }

    public cancel(reason: any = null): this {
        this.reject(reason);
        return this;
    }

    protected finish(): void {
        this._onProgress.dispose();
        this._onFinally.dispose();

        this._options = null;
        this._resolve = null;
        this._reject = null;
        this._onProgress = null;
        this._onFinally = null;
        (this as Mutable<this>).promise = null;
    }

    protected abstract defaultOptions(): Options;
}

type LoaderLookup<
    T extends AbstractLoader<any, any>,
    O extends AbstractLoaderOptions = AbstractLoaderOptions> = {
        [P in keyof LoaderMap]: new (url: string, options?: O) => T;
    }

export const loaders: LoaderLookup<any> = {} as any;

export function registerLoader<
    T extends keyof LoaderMap,
    O extends AbstractLoaderOptions>(key: T, loader: new (url: string, options?: O) => LoaderMap[T]): void {
    loaders[key] = loader as any;
}

export function load<T extends keyof LoaderMap>(loader: T, url: string, options: LoaderOptionsMap[T] = null): LoaderMap[T] {
    var result = new loaders[loader](url, options);
    return result;
}

export interface RejectReason {
    loader: AbstractLoader<any, any>;
    reason: any;
}